import { Injectable } from "@angular/core";
import { Actions, concatLatestFrom, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { ActionWithPayload } from "@obg/ngrx-utils";
import { PlatformService } from "@obg/ngx.global";
import { asyncScheduler, merge, of, timer } from "rxjs";
import { catchError, delayWhen, exhaustMap, filter, map, observeOn, switchMap, takeUntil, tap } from "rxjs/operators";

import { AppError } from "../app-error/app-error.model";
import { AUTH_ACTION_TYPE, AuthAction } from "../auth/auth.action";
import { AuthSelector } from "../auth/auth.selector";
import { SESSION_ACTION_TYPE } from "../auth/session.action";
import { MessageAction } from "../message/message.action";
import { MessageScopeType, MessageType } from "../message/message.model";
import { MessageService } from "../message/message.service";
import { CoreState } from "../state";

import { RealityCheckSelector } from "./reality-check/reality-check.selector";
import { RealityCheckService } from "./reality-check/reality-check.service";
import { RESPONSIBLE_GAMING_ACTION_TYPE, ResponsibleGamingAction } from "./responsible-gaming.action";
import { ResponsibleGamingConfigService } from "./responsible-gaming.config";
import { RESPONSIBLE_GAMING_MESSAGE_CONTEXT, STL_RC_OVERLAPPING_IN_MS } from "./responsible-gaming.const";
import {
	InstantSelfExclusionRequest,
	SelfExclusionRequest,
	StartSessionLimitTimerPayload,
} from "./responsible-gaming.model";
import { ResponsibleGamingService } from "./responsible-gaming.service";

/**
 * @deprecated [fabric-deprecated] - Use ResponsibleGamingService from app-responsible-gaming instead
 */
@Injectable()
export class ResponsibleGamingEffect {
	/**
	 * @deprecated [fabric-deprecated] - Use ResponsibleGamingAppCommand.UpdateSelfExclusionPeriod instead
	 */
	updateSelfExclusionPeriod$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RESPONSIBLE_GAMING_ACTION_TYPE.updateSelfExclusionPeriod),
			map((x: ActionWithPayload<SelfExclusionRequest>) => x.payload),
			switchMap((data) =>
				this.service.updateSelfExclusionPeriod(data).pipe(
					map(() => this.action.updateSelfExclusionPeriodSuccess()),
					catchError((error: AppError) => of(this.action.updateSelfExclusionPeriodFail(error)))
				)
			)
		)
	);

	/**
	 * @deprecated [fabric-deprecated] - Use ResponsibleGamingAppCommand.UpdateInstantSelfExclusionPeriod instead
	 */
	updateInstantSelfExclusionPeriod$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RESPONSIBLE_GAMING_ACTION_TYPE.updateInstantSelfExclusionPeriod),
			map((x: ActionWithPayload<InstantSelfExclusionRequest>) => x.payload),
			switchMap((data) =>
				this.service.updateInstantSelfExclusionPeriod(data).pipe(
					map(() => this.action.updateInstantSelfExclusionPeriodSuccess()),
					catchError((error: AppError) => of(this.action.updateInstantSelfExclusionPeriodFail(error)))
				)
			)
		)
	);

	loginAlert$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				SESSION_ACTION_TYPE.loginSuccess,
				SESSION_ACTION_TYPE.loginWithTokenSuccess,
				SESSION_ACTION_TYPE.loginWithPersonalNumberSuccess,
				SESSION_ACTION_TYPE.loginWithVerificationCodeSuccess,
				SESSION_ACTION_TYPE.loginWithCookieSuccess
			),
			observeOn(asyncScheduler),
			filter(() => this.config.getIsLoginAlertEnabled()),
			map(() =>
				this.messageAction.add({
					context: RESPONSIBLE_GAMING_MESSAGE_CONTEXT.loginAlert,
					scope: MessageScopeType.Local,
					type: MessageType.Info,
					messageCode: "sga-login-alert.message-no-limits",
				})
			)
		)
	);

	logoutOnSelfExclusionSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				RESPONSIBLE_GAMING_ACTION_TYPE.updateSelfExclusionPeriodSuccess,
				RESPONSIBLE_GAMING_ACTION_TYPE.updateCoolOffPeriodSuccess,
				RESPONSIBLE_GAMING_ACTION_TYPE.updateInstantSelfExclusionPeriodSuccess
			),
			map(() => this.authAction.logout())
		)
	);

	startSessionLimitCheck$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SESSION_ACTION_TYPE.loginSuccess),
			observeOn(asyncScheduler),
			concatLatestFrom(() => this.store.select(AuthSelector.getSessionLimitCheckPeriodInMilis)),
			map(([, x]) => this.getCheckPeriod(x)),
			map((checkPeriod) =>
				this.action.startSessionLimitTimer({
					checkPeriod,
					delay: checkPeriod,
				})
			)
		)
	);

	/**
	 * @deprecated [fabric-deprecated] - Use ResponsibleGamingAppCommand.UpdateCoolOffPeriod instead
	 */
	updateCoolOffPeriod$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RESPONSIBLE_GAMING_ACTION_TYPE.updateCoolOffPeriod),
			map((x: ActionWithPayload<SelfExclusionRequest>) => x.payload),
			switchMap((data) =>
				this.service.updateCoolOffPeriod(data).pipe(
					map(() => this.action.updateCoolOffPeriodSuccess()),
					catchError((error: AppError) => of(this.action.updateCoolOffPeriodFail(error)))
				)
			)
		)
	);

	continueSessionLimitCheck$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SESSION_ACTION_TYPE.verifySuccess),
			concatLatestFrom(() => this.store.select(AuthSelector.getSessionLimitCheckPeriodInMilis)),
			map(([, x]) => this.getCheckPeriod(x)),
			switchMap((checkPeriod) =>
				this.service.getSessionLimitTimerLeftTime(checkPeriod).pipe(
					map((delay) => this.action.startSessionLimitTimer({ delay, checkPeriod })),
					catchError((error: AppError) => of(this.action.getSessionLimitCheckPeriodFail(error)))
				)
			)
		)
	);

	sessionLimitCheckTimer$ = this.platformService.forClient(() =>
		createEffect(() =>
			this.actions$.pipe(
				ofType(RESPONSIBLE_GAMING_ACTION_TYPE.startSessionLimitTimer),
				map((x: ActionWithPayload<StartSessionLimitTimerPayload>) => x.payload),
				filter((payload) => payload.checkPeriod > 0), // don't start timer if session time limit is not set
				tap(() => this.service.storeSessionLimitTimerStartDate(new Date())),
				exhaustMap((payload) =>
					timer(payload.delay, payload.checkPeriod).pipe(
						switchMap(() => this.service.getNewSessionDuration(payload.checkPeriod)),
						map((newSessionDurationMinutes) => this.action.checkSessionLimit(newSessionDurationMinutes)),
						takeUntil(this.actions$.pipe(ofType(RESPONSIBLE_GAMING_ACTION_TYPE.stopSessionLimitCheck)))
					)
				)
			)
		)
	);

	stopSessionLimitCheck$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.sessionEnd),
			map(() => this.action.stopSessionLimitCheck())
		)
	);

	sessionLimitExpired$ = this.platformService.forClient(() =>
		createEffect(() =>
			this.actions$.pipe(
				ofType(RESPONSIBLE_GAMING_ACTION_TYPE.checkSessionLimit),
				map((x: ActionWithPayload<number>) => x.payload),
				concatLatestFrom(() => [
					this.store.select(this.realityCheckSelector.nextPulse()),
					this.store.select(this.realityCheckSelector.triggeredAt()),
				]),
				// wait if reality check should be visible soon
				delayWhen(([, nextPulse]) =>
					timer(this.realityCheckService.getDelayUntilNextRealityCheck(nextPulse, STL_RC_OVERLAPPING_IN_MS))
				),
				// if there was a delay, StateUpdate holds the outdated state, so we need to get the current state
				// to decide if RC was triggered indeed
				// don't show if reality check was shown recently
				filter(
					([, , realityCheckTriggeredAt]) =>
						!this.realityCheckService.wasRealityCheckTriggeredWithinInterval(
							realityCheckTriggeredAt!,
							STL_RC_OVERLAPPING_IN_MS
						)
				),
				map(([newSessionDurationMinutes]) => this.action.sessionLimitExpired(newSessionDurationMinutes))
			)
		)
	);

	/**
	 * @deprecated [fabric-deprecated] - Use ResponsibleGamingAppCommand.StoreSessionDuration instead
	 */
	storeSessionDuration$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(RESPONSIBLE_GAMING_ACTION_TYPE.sessionLimitExpired),
				map((x: ActionWithPayload<number>) => x.payload),
				tap((sessionDuration) => this.service.storeSessionDuration(sessionDuration))
			),
		{ dispatch: false }
	);

	/**
	 * @deprecated [fabric-deprecated] - Use ResponsibleGamingAppCommand.ResetSessionDuration instead
	 */
	resetSessionDuration$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(SESSION_ACTION_TYPE.loginSuccess, RESPONSIBLE_GAMING_ACTION_TYPE.stopSessionLimitCheck),
				tap(() => this.service.storeSessionDuration(0))
			),
		{ dispatch: false }
	);

	initMessages$ = createEffect(() =>
		merge(
			this.messageService.mapErrorByContext$({
				[RESPONSIBLE_GAMING_ACTION_TYPE.updateSelfExclusionPeriodFail]:
					RESPONSIBLE_GAMING_MESSAGE_CONTEXT.updateSelfExclusion,
				[RESPONSIBLE_GAMING_ACTION_TYPE.getSessionLimitCheckPeriodFail]:
					RESPONSIBLE_GAMING_MESSAGE_CONTEXT.getSessionLimitCheckPeriod,
				[RESPONSIBLE_GAMING_ACTION_TYPE.updateCoolOffPeriodFail]:
					RESPONSIBLE_GAMING_MESSAGE_CONTEXT.updateCoolOff,
			}),
			this.messageService.mapClearByContext$({
				[RESPONSIBLE_GAMING_ACTION_TYPE.updateSelfExclusionPeriod]:
					RESPONSIBLE_GAMING_MESSAGE_CONTEXT.updateSelfExclusion,
				[SESSION_ACTION_TYPE.loginSuccess]: RESPONSIBLE_GAMING_MESSAGE_CONTEXT.getSessionLimitCheckPeriod,
				[SESSION_ACTION_TYPE.verifySuccess]: RESPONSIBLE_GAMING_MESSAGE_CONTEXT.getSessionLimitCheckPeriod,
			})
		)
	);

	constructor(
		private actions$: Actions,
		private store: Store<CoreState>,
		private realityCheckSelector: RealityCheckSelector,
		private action: ResponsibleGamingAction,
		private messageAction: MessageAction,
		private service: ResponsibleGamingService,
		private realityCheckService: RealityCheckService,
		private authAction: AuthAction,
		private config: ResponsibleGamingConfigService,
		private messageService: MessageService,
		private platformService: PlatformService
	) {}

	private getCheckPeriod(checkPeriod: number | undefined): number {
		return this.config.getSessionLimitIsEnabled() && checkPeriod ? checkPeriod : 0;
	}
}
